import { Controller } from "@hotwired/stimulus"

/**
 * Responsive Form Controller
 * Handles responsive layouts for forms with Tailwind CSS,
 * dynamically adjusting row arrangements based on screen size.
 */
export default class extends Controller {
  static targets = ["row", "field"]
  static values = {
    debug: { type: Boolean, default: false }
  }

  connect() {
    if (this.debugValue) {
      console.log("Responsive form controller connected")
    }
    this.adjustLayout()
    this.resizeObserver = new ResizeObserver(entries => this.adjustLayout())
    this.resizeObserver.observe(document.body)

    // Listen for window resize events
    window.addEventListener('resize', this.handleResize.bind(this))
  }

  disconnect() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
    }
    window.removeEventListener('resize', this.handleResize)
  }

  handleResize() {
    this.adjustLayout()
  }

  adjustLayout() {
    // Apply Tailwind responsive classes instead of manual switching
    this.rowTargets.forEach(row => {
      // Ensure the row has the correct grid classes
      this.ensureGridClass(row)

      // Update field layouts if needed
      const fields = row.querySelectorAll('[data-responsive-form-target="field"]')
      fields.forEach(field => this.applyFieldClasses(field))
    })
  }

  ensureGridClass(row) {
    // Make sure the row has the basic Tailwind grid classes
    // If it doesn't have the tw-grid class, add the entire responsive grid setup
    if (!row.classList.contains('tw-grid')) {
      row.classList.add(
        'tw-grid',
        'tw-grid-cols-1',  // Mobile: 1 column (stack)
        'tw-md:grid-cols-2', // Medium screens: 2 columns
        'tw-lg:grid-cols-3', // Large screens: 3 columns
        'tw-gap-6'           // Default gap
      )
    }
  }

  applyFieldClasses(field) {
    // Get custom column spans from data attributes
    const mdSpan = field.dataset.mdCols || '1'
    const lgSpan = field.dataset.lgCols || '1'
    const xlSpan = field.dataset.xlCols || lgSpan // Default to lgSpan if not specified

    // Remove any existing column span classes
    const classesToRemove = []
    for (let i = 0; i < field.classList.length; i++) {
      const cls = field.classList[i]
      if (cls.match(/tw-(sm|md|lg|xl):col-span-\d+/)) {
        classesToRemove.push(cls)
      }
    }
    classesToRemove.forEach(cls => field.classList.remove(cls))

    // Add responsive column span classes
    if (mdSpan !== '1') {
      field.classList.add(`tw-md:col-span-${mdSpan}`)
    }
    if (lgSpan !== '1') {
      field.classList.add(`tw-lg:col-span-${lgSpan}`)
    }
    if (xlSpan !== '1') {
      field.classList.add(`tw-xl:col-span-${xlSpan}`)
    }

    // Full-width fields
    if (field.hasAttribute('data-full-width')) {
      field.classList.add('tw-col-span-full')
    }
  }

  // Helper method that can be called from views
  fullWidth(event) {
    const field = event.currentTarget
    field.classList.add('tw-col-span-full')
    field.setAttribute('data-full-width', 'true')
  }

  // Helper method that can be called from views
  standardWidth(event) {
    const field = event.currentTarget
    field.classList.remove('tw-col-span-full')
    field.removeAttribute('data-full-width')
  }
}